'use client';

import { usePathname } from 'next/navigation';
import { createContext, useContext, useEffect } from 'react';

// Types
import { Context, ProviderProps } from '@/types/contexts/siteContext';

// Constants
import {
  LeftToRightDirValue,
  RightToLeftDirValue,
  DataTypeAttributeName,
} from '@/constants/configuration';

// Services
import { rules } from '@/services/pages/rules';

export const SiteContext = createContext<Context>({
  languages: [],
  language: 'en-gb',
  reusableContent: undefined,
});

export const SiteProvider = ({
  language,
  children,
  languages,
  reusableContent,
}: ProviderProps) => {
  const pathName = usePathname();

  useEffect(() => {
    const languageDetails = languages.find(
      ({ fullLanguageCode }) => fullLanguageCode === language,
    );

    document.documentElement.lang = language;
    document.documentElement.dir = languageDetails?.isRightToLeft
      ? RightToLeftDirValue
      : LeftToRightDirValue;
  }, []);

  useEffect(() => {
    document.body.removeAttribute(DataTypeAttributeName);

    const match = rules.find((rule) => new RegExp(rule.pattern).test(pathName));

    if (!match || !match.bodyDataType) {
      return;
    }

    document.body.setAttribute(DataTypeAttributeName, match.bodyDataType);
  }, [pathName]);

  return (
    <SiteContext.Provider
      value={{
        language: language,
        languages: languages,
        reusableContent: reusableContent,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};

export const useSiteContext = () => useContext<Context>(SiteContext);
